/** Cliente HTTP do painel. Cookie HttpOnly + cabeçalho de workspace ativo. */

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;
  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

let activeWorkspace: string | null = null;

export function setActiveWorkspace(id: string | null): void {
  activeWorkspace = id;
}

export function getActiveWorkspace(): string | null {
  return activeWorkspace;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  workspace?: string | null;
  raw?: boolean;
  signal?: AbortSignal;
}

export async function api<T = unknown>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {};
  const workspace = options.workspace === undefined ? activeWorkspace : options.workspace;
  if (workspace) headers['X-Orbs-Workspace'] = workspace;
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';

  const response = await fetch(path, {
    method: options.method ?? (options.body !== undefined ? 'POST' : 'GET'),
    headers,
    credentials: 'same-origin',
    ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
    ...(options.signal ? { signal: options.signal } : {}),
  });

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  let payload: unknown = null;
  try {
    payload = text ? JSON.parse(text) : null;
  } catch {
    payload = text;
  }

  if (!response.ok) {
    const data = (payload ?? {}) as { error?: string; code?: string; details?: unknown };
    throw new ApiError(
      response.status,
      data.code ?? 'HTTP_ERROR',
      data.error ?? `Falha na requisição (${response.status}).`,
      data.details,
    );
  }
  return payload as T;
}

export interface UploadProgress {
  loaded: number;
  total: number;
  percent: number;
}

/**
 * Upload com barra de progresso real (XHR — `fetch` não expõe upload progress).
 * O MIME verdadeiro do arquivo vai no cabeçalho e é reconferido no servidor.
 */
/**
 * Arquivos acima deste tamanho vão em pedaços. Proxies de hospedagem limitam
 * o corpo de uma requisição (a Hostinger corta em 512 MB) e derrubam
 * conexões longas; fatiar resolve os dois problemas e permite retomar.
 */
export const CHUNKED_UPLOAD_THRESHOLD = 48 * 1024 * 1024;

/** Limiar efetivo; `localStorage['orbs:chunkThreshold']` permite forçar o caminho fatiado em testes. */
function chunkThreshold(): number {
  try {
    const raw = window.localStorage.getItem('orbs:chunkThreshold');
    if (raw !== null && Number.isFinite(Number(raw))) return Number(raw);
  } catch { /* storage indisponível */ }
  return CHUNKED_UPLOAD_THRESHOLD;
}

export function uploadVideoFile(
  videoId: string,
  file: File,
  workspaceId: string,
  onProgress: (progress: UploadProgress) => void,
): { promise: Promise<any>; abort: () => void } {
  if (file.size > chunkThreshold()) return uploadInChunks(videoId, file, workspaceId, onProgress);
  return uploadSingleShot(videoId, file, workspaceId, onProgress);
}

function uploadSingleShot(
  videoId: string,
  file: File,
  workspaceId: string,
  onProgress: (progress: UploadProgress) => void,
): { promise: Promise<any>; abort: () => void } {
  const xhr = new XMLHttpRequest();
  const promise = new Promise<any>((resolve, reject) => {
    xhr.open('POST', `/api/videos/${encodeURIComponent(videoId)}/upload`);
    xhr.withCredentials = true;
    xhr.setRequestHeader('X-Orbs-Workspace', workspaceId);
    xhr.setRequestHeader('X-File-Name', encodeHeader(file.name));
    xhr.setRequestHeader('X-File-Type', file.type || 'application/octet-stream');
    xhr.setRequestHeader('X-File-Size', String(file.size));
    xhr.setRequestHeader('Content-Type', 'application/octet-stream');

    xhr.upload.addEventListener('progress', (event) => {
      if (!event.lengthComputable) return;
      onProgress({ loaded: event.loaded, total: event.total, percent: (event.loaded / event.total) * 100 });
    });
    xhr.addEventListener('load', () => {
      let data: any = null;
      try {
        data = JSON.parse(xhr.responseText);
      } catch {
        data = null;
      }
      if (xhr.status >= 200 && xhr.status < 300) resolve(data);
      else reject(new ApiError(xhr.status, data?.code ?? 'UPLOAD_ERROR', data?.error ?? 'Falha no upload.', data?.details));
    });
    xhr.addEventListener('error', () => reject(new ApiError(0, 'NETWORK', 'Conexão interrompida durante o upload.')));
    xhr.addEventListener('abort', () => reject(new ApiError(0, 'ABORTED', 'Upload cancelado.')));
    xhr.send(file);
  });
  return { promise, abort: () => xhr.abort() };
}

/* ------------------------------------------------------------------ */
/* Upload em pedaços                                                    */
/* ------------------------------------------------------------------ */

const CHUNK_MAX_ATTEMPTS = 4;

function sendChunk(
  url: string,
  blob: Blob,
  workspaceId: string,
  signal: { aborted: boolean; current: XMLHttpRequest | null },
  onLoaded: (bytes: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    signal.current = xhr;
    xhr.open('PUT', url);
    xhr.withCredentials = true;
    xhr.setRequestHeader('X-Orbs-Workspace', workspaceId);
    xhr.setRequestHeader('Content-Type', 'application/octet-stream');
    xhr.upload.addEventListener('progress', (event) => { if (event.lengthComputable) onLoaded(event.loaded); });
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) { onLoaded(blob.size); resolve(); return; }
      let data: any = null;
      try { data = JSON.parse(xhr.responseText); } catch { data = null; }
      reject(new ApiError(xhr.status, data?.code ?? 'CHUNK_ERROR', data?.error ?? `Falha ao enviar um pedaço (HTTP ${xhr.status}).`));
    });
    xhr.addEventListener('error', () => reject(new ApiError(0, 'NETWORK', 'Conexão interrompida durante o upload.')));
    xhr.addEventListener('abort', () => reject(new ApiError(0, 'ABORTED', 'Upload cancelado.')));
    xhr.send(blob);
  });
}

function uploadInChunks(
  videoId: string,
  file: File,
  workspaceId: string,
  onProgress: (progress: UploadProgress) => void,
): { promise: Promise<any>; abort: () => void } {
  const signal = { aborted: false, current: null as XMLHttpRequest | null };
  const base = `/api/videos/${encodeURIComponent(videoId)}/upload`;

  const promise = (async () => {
    const session = await api<{ uploadId: string; chunkSize: number; totalChunks: number }>(`${base}/init`, {
      method: 'POST',
      body: { name: file.name, type: file.type || 'application/octet-stream', size: file.size },
    });

    let doneBytes = 0;
    const report = (inFlight: number): void => {
      const loaded = Math.min(file.size, doneBytes + inFlight);
      onProgress({ loaded, total: file.size, percent: (loaded / file.size) * 100 });
    };

    for (let index = 0; index < session.totalChunks; index++) {
      if (signal.aborted) throw new ApiError(0, 'ABORTED', 'Upload cancelado.');
      const start = index * session.chunkSize;
      const blob = file.slice(start, Math.min(file.size, start + session.chunkSize));
      const url = `${base}/${session.uploadId}/chunk/${index}`;

      // Queda de conexão num pedaço não derruba o envio inteiro: tenta de novo.
      for (let attempt = 1; ; attempt++) {
        try {
          await sendChunk(url, blob, workspaceId, signal, (bytes) => report(bytes));
          break;
        } catch (err) {
          const retriable = err instanceof ApiError && (err.status === 0 || err.status >= 500 || err.status === 429) && err.code !== 'ABORTED';
          if (!retriable || attempt >= CHUNK_MAX_ATTEMPTS || signal.aborted) throw err;
          await new Promise((r) => setTimeout(r, 600 * attempt * attempt));
        }
      }
      doneBytes += blob.size;
      report(0);
    }

    return api<any>(`${base}/${session.uploadId}/complete`, { method: 'POST' });
  })();

  return {
    promise,
    abort: () => {
      signal.aborted = true;
      signal.current?.abort();
    },
  };
}

export function uploadThumbnail(videoId: string, file: File, workspaceId: string): Promise<any> {
  return fetch(`/api/videos/${encodeURIComponent(videoId)}/thumbnail`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'X-Orbs-Workspace': workspaceId, 'Content-Type': file.type || 'application/octet-stream' },
    body: file,
  }).then(async (response) => {
    const data = await response.json().catch(() => null);
    if (!response.ok) throw new ApiError(response.status, data?.code ?? 'UPLOAD_ERROR', data?.error ?? 'Falha ao enviar a imagem.');
    return data;
  });
}

/** Cabeçalhos HTTP só aceitam ASCII; nomes com acento vão codificados. */
function encodeHeader(value: string): string {
  return /^[\x20-\x7E]*$/.test(value) ? value : encodeURIComponent(value);
}
